import * as React from 'react';
import {
  Expressions,
  TypeImplementation,
  Component,
} from '@packages/systems/wfdl';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {updateExpressionAction} from '@packages/systems/ExpressionEditor-utils';
import {TBoolean, EBoolean} from '@packages/systems/wfdl-ast';
import {
  selectCurrentPage,
  selectSelectedElement,
  selectCurrentComponent,
  getPluginsFromState,
  selectComponents,
} from '@packages/utilities/platform-selectors';
import {getElementLabel} from '@packages/utilities/platform';
import {isSlot} from '@packages/utilities/elementTypeUtils';
import {
  isNeverSearchIndexable,
  hasAncestorExcludedFromSearch,
} from '@packages/utilities/platform-search';
import {isDynamoWrapperWithRandomSort} from '@packages/systems/dynamo/utils/DynamoElementUtils';
import TooltipTrigger from '@packages/systems/design-system/TooltipTrigger';
import {Checkbox, Text} from '@packages/systems/spring';
import {useDesignerSelector} from '@packages/systems/designer/flux';

const SearchIndexBooleanContainer = ({
  expr,
  dispatch,
}: {
  expr: ContextualExpression;
  dispatch: TypeImplementation.ExpressionEditorDispatch;
}) => {
  const page = useDesignerSelector(selectCurrentPage);
  const componentMap = useDesignerSelector(selectComponents);
  const currentComponent = useDesignerSelector(selectCurrentComponent);
  const selectedElement = useDesignerSelector(selectSelectedElement);
  const plugins = useDesignerSelector(getPluginsFromState);

  const elExcluded = ContextualExpressions.getBoolean(stripAnn(expr));

  const ancestorExcluded = React.useMemo(() => {
    const compRender = Component.getRender(currentComponent);
    const root = compRender ? Expressions.getElement(compRender) : null;
    if (!root || !selectedElement) return false;

    return hasAncestorExcludedFromSearch(root, selectedElement);
  }, [currentComponent, selectedElement]);

  const pageExcluded = React.useMemo(() => page && page.searchExclude, [page]);

  const isElementNeverIndexable = isNeverSearchIndexable(selectedElement);

  const label = React.useMemo(() => {
    const compRender = Component.getRender(currentComponent);
    return selectedElement && isSlot(selectedElement)
      ? 'slot'
      : selectedElement
        ? getElementLabel(selectedElement, compRender, componentMap, plugins)
        : 'element';
  }, [componentMap, currentComponent, selectedElement, plugins]);

  const disabled = pageExcluded || ancestorExcluded || isElementNeverIndexable;

  const onChange = React.useCallback(
    // @ts-expect-error - TS7006 - Parameter 'e' implicitly has an 'any' type.
    (e) => {
      if (!disabled) {
        dispatch(updateExpressionAction(EBoolean(e.target.checked)));
      }
    },
    [disabled, dispatch]
  );

  const checked = Boolean(
    pageExcluded || ancestorExcluded || elExcluded || isElementNeverIndexable
  );

  let disabledMessage;
  if (isElementNeverIndexable) {
    if (isDynamoWrapperWithRandomSort(selectedElement)) {
      disabledMessage =
        'Collection Lists with a Random sort order cannot be indexed.';
    } else {
      disabledMessage = 'This element cannot be indexed.';
    }
  } else if (pageExcluded) {
    disabledMessage = 'This content has been excluded in its Page settings.';
  } else if (ancestorExcluded) {
    disabledMessage =
      'This content has been excluded in a parent element’s settings.';
  }

  const checkbox = (
    <Checkbox
      isChecked={checked}
      onChange={onChange}
      isDisabled={disabled}
      data-automation-id="search-index-exclude-input"
      label={
        <Text colorVariant="secondary">
          {`Exclude ${label}’s content from site search results`}
        </Text>
      }
      // label={`Exclude ${label}’s content from site search results`}
    />
  );

  return disabledMessage ? wrapInTooltip(checkbox, disabledMessage) : checkbox;
};

const wrapInTooltip = (component: React.ReactElement<any>, message: string) => {
  return (
    <TooltipTrigger
      alternate
      copy
      panel
      placement="bottom"
      arrowSize={0}
      content={message}
    >
      <div>{component}</div>
    </TooltipTrigger>
  );
};

export const SearchIndexBoolean = TypeImplementation.Alias({
  type: TBoolean,
  render: SearchIndexBooleanContainer,
});
